import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import { keyframes } from '@mui/material/styles';
import type { ReactNode } from 'react';
import askAiOn from '../../../../assets/alison-run/ask-ai-on.svg';
import { useAlisonRun } from '../AlisonRunProvider.js';
import { fillTemplate } from '../data/alisonRun.js';
import { ALISON_BORDER_GRADIENT, ALISON_FLOATING_SHADOW } from './alisonRunColors.js';

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.35; }
`;
const LABEL_SEPARATOR = ' · ';

export function AlisonCornerPill(): ReactNode {
  const { pill, copy, introOpen, drawerOpen, resultsOpen, openIntro, openDrawer, openResults } =
    useAlisonRun();
  if (pill === null || introOpen || drawerOpen || resultsOpen) return null;

  const label =
    pill.kind === 'entry'
      ? copy.title
      : pill.kind === 'running'
        ? fillTemplate(copy.runningPill, { current: pill.current, total: pill.total })
        : pill.kind === 'failed'
          ? fillTemplate(copy.failedPill, { title: pill.title })
          : pill.count === null || pill.count <= 1
            ? fillTemplate(copy.donePill, { outcome: pill.outcome })
            : fillTemplate(copy.donePillCount, {
                count: pill.count,
                outcome: pill.outcome.toLowerCase(),
              });
  const separatorIndex = label.indexOf(LABEL_SEPARATOR);
  const emphasis = separatorIndex < 0 ? label : label.slice(0, separatorIndex);
  const detail = separatorIndex < 0 ? '' : label.slice(separatorIndex);
  const isDone = pill.kind === 'done';
  const ended = isDone || pill.kind === 'failed';

  return (
    <ButtonBase
      onClick={pill.kind === 'entry' ? openIntro : pill.kind === 'done' ? openResults : openDrawer}
      sx={{
        position: 'fixed',
        right: '33px',
        bottom: '31px',
        zIndex: (theme) => theme.zIndex.drawer - 1,
        gap: ended ? '8px' : 0,
        pl: ended ? '20px' : '12px',
        pr: '20px',
        py: '5px',
        minHeight: 44,
        border: '1px solid transparent',
        borderRadius: '32px',
        background: (theme) =>
          `linear-gradient(${theme.palette.background.paper}, ${theme.palette.background.paper}) padding-box, ${ALISON_BORDER_GRADIENT} border-box`,
        boxShadow: ALISON_FLOATING_SHADOW,
        color: 'background.icon',
        fontSize: ended ? 12.5 : 14,
        lineHeight: 'normal',
        whiteSpace: 'nowrap',
      }}
    >
      {pill.kind === 'entry' && (
        <Box component="img" src={askAiOn} alt="" sx={{ width: 32, height: 32, flexShrink: 0 }} />
      )}
      {pill.kind === 'running' && (
        <Box sx={{ width: 32, height: 32, flexShrink: 0, display: 'grid', placeItems: 'center' }}>
          <Box
            sx={{
              width: '10px',
              height: '10px',
              borderRadius: '50%',
              bgcolor: 'primary.main',
              animation: `${pulse} 1.2s ease-in-out infinite`,
            }}
          />
        </Box>
      )}
      {ended && (
        <Box
          sx={{
            width: '9px',
            height: '9px',
            flexShrink: 0,
            borderRadius: '50%',
            bgcolor: isDone ? 'success.main' : 'error.main',
          }}
        />
      )}
      <Box component="span">
        <Box component="span" sx={{ fontWeight: ended ? 600 : 700 }}>
          {emphasis}
        </Box>
        {detail !== '' && (
          <Box component="span" sx={{ fontWeight: 400 }}>
            {detail}
          </Box>
        )}
      </Box>
    </ButtonBase>
  );
}
